import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { i18n } from 'shared/i18n';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { tagQueryKeys, TagsByDocumentResponse } from 'entities/tag';
import { CustomEditor } from 'slate/types/slate';
import { registerTagSplitHandlers } from '../splitHandlers';

export const useTagSplitContinuation = (editor: CustomEditor, documentSuperId: number | undefined, canEditTags: boolean | undefined): void => {
  const queryClient = useQueryClient();
  const latest = useRef({ documentSuperId, canEditTags });
  latest.current = { documentSuperId, canEditTags };

  useEffect(() => {
    const cachedTag = (tagId: string) => {
      const cached = queryClient.getQueriesData<TagsByDocumentResponse>({ queryKey: [tagQueryKeys.tagsByDocument, latest.current.documentSuperId] });
      for (const [, data] of cached) {
        const tag = data?.tags?.find((t) => t.id === tagId);
        if (tag) return tag;
      }
      return undefined;
    };

    return registerTagSplitHandlers(editor, {
      continuationBlocker: (tagId) => {
        if (latest.current.canEditTags === false) return 'permission';
        const tag = cachedTag(tagId);
        if (tag?.hidden) return 'hidden';
        return tag?.type === 'nonFraction' || tag?.type === 'fraction' ? 'numeric' : null;
      },
      onTagSplit: ({ blocked }) => {
        const reason = blocked[0]?.reason;
        if (reason && reason !== 'unsupported') {
          dispatchMessage(EventType.ADD_MESSAGE, { type: 'warning', text: i18n(`tag:continuation.split.${reason}`), removeTime: 6000 });
        }
      },
    });
  }, [editor, queryClient]);
};
